// The one generic `localStorage` wrapper (#4397, epic #4391): read and write
// wrapped in try/catch, a caller-given zod schema and fallback so a corrupt
// or unavailable store degrades to a KNOWN GOOD value rather than throwing
// through a render.
//
// NAMED `local-pref.ts`, NOT `localPref.ts`. The issue spelled it camelCase;
// `scripts/file-naming.test.ts`'s `module` rule governs every other `.ts` in
// this app and requires kebab-case, with a `debt: 53` ratchet that MAY ONLY
// GO DOWN — a new camelCase file raises it to 54 and fails the gate. Renamed
// on sight rather than fought, the same correction this epic already made for
// `terminalEvents.ts` -> `terminal-events.ts` (#4395).
//
// THE ONLY EXISTING PRECEDENT before this file: `components/ThemeToggle.tsx`
// (`toggle`, ~line 32) does one inline `try { localStorage.setItem(...) } catch {}`
// for `dz-theme` — read-only recovery (the static script in `index.html`
// re-reads it before paint), one key, one call site. This module generalizes
// that posture for a caller that needs to READ its own stored value back
// (the pane layout does, on every mount) and needs the value's SHAPE proved,
// not merely its presence — `JSON.parse` succeeding says nothing about
// whether the result is a `TerminalLayout`.
//
// `localStorage` itself, not merely `.getItem`/`.setItem`, can throw or be
// absent: a private-mode browser can throw on the PROPERTY ACCESS
// (`window.localStorage`) before any method call, which is why this reads it
// through `globalThis.localStorage` inside the same try block rather than
// caching a reference at module scope.

import type { z } from 'zod';

/**
 * Read `key`, parsed through `schema`. Returns `fallback` for: storage
 * unavailable (throws or absent), no stored value, invalid JSON, or JSON that
 * parses but does not match `schema` — every one of those is "nothing usable
 * was stored," and a caller should not have to tell them apart.
 */
export function readLocalPref<T>(key: string, schema: z.ZodType<T>, fallback: T): T {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    if (raw === null || raw === undefined) return fallback;
    const parsed: unknown = JSON.parse(raw);
    const result = schema.safeParse(parsed);
    return result.success ? result.data : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Write `value` under `key`. Silently does nothing on a storage failure
 * (quota exceeded, private-mode throw, no `localStorage` at all) — the
 * caller's in-memory state is still correct; only persistence across a
 * reload is lost, and only for that session.
 */
export function writeLocalPref<T>(key: string, value: T): void {
  try {
    globalThis.localStorage?.setItem(key, JSON.stringify(value));
  } catch {
    // ponytail: quota/private-mode throw; the caller's state stays correct in
    // memory, it just won't survive a reload this session.
  }
}

/**
 * Remove `key`. Silently does nothing on a storage failure, like
 * {@link writeLocalPref}: nothing stored is the state the caller asked for.
 */
export function clearLocalPref(key: string): void {
  try {
    globalThis.localStorage?.removeItem(key);
  } catch {
    // Storage unavailable: there is nothing it could still hold for us.
  }
}
